import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHelpCircle, FiCheck, FiX, FiRefreshCw, FiAward } = FiIcons;

const LessonQuiz = ({ quiz, attempts, onSubmit, onComplete }) => {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);

  // Load quiz questions (mock data for now)
  useEffect(() => {
    // In a real implementation, you'd fetch questions from the quiz
    const mockQuestions = [
      {
        id: '1',
        type: 'mc',
        prompt: 'Which of the following is a macronutrient?',
        choices: ['Vitamin C', 'Protein', 'Iron', 'Calcium'],
        answer: 'Protein',
        explanation: 'Protein is one of the three main macronutrients, along with carbohydrates and fats.'
      },
      {
        id: '2',
        type: 'mc',
        prompt: 'What is the recommended daily water intake for adults?',
        choices: ['4-6 cups', '6-8 cups', '8-10 cups', '10-12 cups'],
        answer: '8-10 cups',
        explanation: 'Most health organizations recommend 8-10 cups (64-80 oz) of water per day for adults.'
      },
      {
        id: '3',
        type: 'sr',
        prompt: 'Name one benefit of eating fiber-rich foods.',
        answer: 'Improved digestion',
        explanation: 'Fiber helps with digestion, blood sugar control, and maintaining healthy cholesterol levels.'
      }
    ];
    
    setQuestions(mockQuestions);
    
    // Check if user has already completed the quiz
    if (attempts.length > 0) {
      const lastAttempt = attempts[attempts.length - 1];
      setAnswers(lastAttempt.answers);
      setScore(lastAttempt.score);
      setSubmitted(true);
      setShowResults(true);
    }
  }, [quiz, attempts]);

  const handleAnswerChange = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const calculateScore = () => {
    let correct = 0;
    questions.forEach(question => {
      const userAnswer = answers[question.id];
      if (question.type === 'mc') {
        if (userAnswer === question.answer) correct++;
      } else if (question.type === 'sr') {
        // Simple check for short response - in reality, this would be more sophisticated
        if (userAnswer && userAnswer.toLowerCase().includes(question.answer.toLowerCase())) {
          correct++;
        }
      }
    });
    
    return Math.round((correct / questions.length) * 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if all questions are answered
    const unansweredQuestions = questions.filter(q => !answers[q.id]);
    if (unansweredQuestions.length > 0) {
      alert('Please answer all questions before submitting.');
      return;
    }

    setLoading(true);
    
    try {
      const calculatedScore = calculateScore();
      setScore(calculatedScore);
      
      // Submit quiz attempt
      await onSubmit(quiz.id, calculatedScore, answers);
      
      setSubmitted(true);
      setShowResults(true);
      
      // Call onComplete callback
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error('Error submitting quiz:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRetake = () => {
    setAnswers({});
    setSubmitted(false);
    setShowResults(false);
    setScore(0);
  };

  const isCorrectAnswer = (question, userAnswer) => {
    if (question.type === 'mc') {
      return userAnswer === question.answer;
    } else if (question.type === 'sr') {
      return userAnswer && userAnswer.toLowerCase().includes(question.answer.toLowerCase());
    }
    return false;
  };

  if (questions.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="text-center">
          <SafeIcon icon={FiHelpCircle} className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-500">Loading quiz...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg border border-gray-200 overflow-hidden"
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiHelpCircle} className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">{quiz.title}</h3>
          </div>
          
          {showResults && (
            <div className="flex items-center space-x-2">
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                score >= 70 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                Score: {score}%
              </div>
              {score >= 70 && (
                <SafeIcon icon={FiAward} className="w-5 h-5 text-yellow-500" />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Quiz Content */}
      <div className="p-6">
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {questions.map((question, index) => (
              <div key={question.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center text-sm font-medium">
                    {index + 1}
                  </span>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 mb-3">
                      {question.prompt}
                    </h4>
                    
                    {question.type === 'mc' ? (
                      <div className="space-y-2">
                        {question.choices.map((choice) => (
                          <label
                            key={choice}
                            className={`flex items-center space-x-3 p-3 rounded border cursor-pointer transition-colors ${
                              showResults
                                ? choice === question.answer
                                  ? 'bg-green-50 border-green-300'
                                  : answers[question.id] === choice && choice !== question.answer
                                  ? 'bg-red-50 border-red-300'
                                  : 'bg-gray-50 border-gray-200'
                                : answers[question.id] === choice
                                ? 'bg-blue-50 border-blue-300'
                                : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                            }`}
                          >
                            <input
                              type="radio"
                              name={question.id}
                              value={choice}
                              checked={answers[question.id] === choice}
                              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                              disabled={submitted}
                              className="text-blue-600 focus:ring-blue-500"
                            />
                            <span className="flex-1">{choice}</span>
                            {showResults && choice === question.answer && (
                              <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-600" />
                            )}
                            {showResults && answers[question.id] === choice && choice !== question.answer && (
                              <SafeIcon icon={FiX} className="w-4 h-4 text-red-600" />
                            )}
                          </label>
                        ))}
                      </div>
                    ) : (
                      <textarea
                        value={answers[question.id] || ''}
                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        disabled={submitted}
                        placeholder="Enter your answer..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                        rows={3}
                      />
                    )}
                    
                    {/* Explanation */}
                    <AnimatePresence>
                      {showResults && question.explanation && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded"
                        >
                          <p className="text-sm text-blue-800">
                            <strong>Explanation:</strong> {question.explanation}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Submit/Retake Button */}
          <div className="mt-6 flex justify-center">
            {!submitted ? (
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiCheck} className="w-4 h-4" />
                    <span>Submit Quiz</span>
                  </>
                )}
              </button>
            ) : (
              <div className="text-center space-y-4">
                <div className={`text-lg font-medium ${
                  score >= 70 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {score >= 70 
                    ? '🎉 Congratulations! You passed the quiz!'
                    : '📚 Keep studying! You can retake this quiz.'
                  }
                </div>
                
                <button
                  onClick={handleRetake}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors flex items-center space-x-2"
                >
                  <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
                  <span>Retake Quiz</span>
                </button>
              </div>
            )}
          </div>
        </form>
      </div>
    </motion.div>
  );
};

export default LessonQuiz;